import { useState } from "react";
import { View, Text, Pressable, Linking } from "react-native";
import { BookOpen, ExternalLink } from "lucide-react-native";
import type { EvidenceSource } from "@louise/content";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@louise/moon-ui/primitives/Accordion";
import { EMBER } from "../../../onboarding/theme";
import EvidenceLevelChip, { EVIDENCE_LEVELS } from "../EvidenceLevelChip";
import EvidenceLevelModal from "../EvidenceLevelModal";
import { semanticColors } from "@louise/theme/tokens";

const LEVEL_ORDER: string[] = [
  "meta-analysis",
  "rct",
  "cohort",
  "observational",
  "review",
  "guideline",
  "official",
  "reference",
  "expert-opinion",
];

const UNGROUPED_KEY = "__ungrouped__";

function groupByLevel(refs: EvidenceSource[]): [string, EvidenceSource[]][] {
  const buckets = new Map<string, EvidenceSource[]>();
  for (const ref of refs) {
    const key = ref.evidenceLevel ?? UNGROUPED_KEY;
    const list = buckets.get(key) ?? [];
    list.push(ref);
    buckets.set(key, list);
  }
  return [...LEVEL_ORDER, UNGROUPED_KEY]
    .filter((key) => buckets.has(key))
    .map((key) => [key, buckets.get(key)!]);
}

function formatCitation(ref: EvidenceSource): string {
  const segments: string[] = [];
  if (ref.authors) segments.push(ref.authors);
  if (ref.year) segments.push(`(${ref.year})`);
  const head = segments.join(" ");
  const trailing = [ref.journal].filter(Boolean).join(", ");
  return [head, trailing].filter(Boolean).join(". ");
}

export default function ReferencesPanel({
  references,
}: {
  references: EvidenceSource[];
}) {
  const [modalLevel, setModalLevel] = useState<string | null>(null);

  const groups = groupByLevel(references);

  return (
    <View
      style={{
        marginTop: 24,
        borderRadius: 16,
        backgroundColor: semanticColors.surface.muted,
        borderWidth: 1,
        borderColor: semanticColors.border.subtle,
        overflow: "hidden",
      }}
    >
      <Accordion type="single" collapsible>
        <AccordionItem value="sources">
          <AccordionTrigger
            analyticsLabel={`Sources (${references.length})`}
            action={{ expand: "expand_sources", collapse: "collapse_sources" }}
            className="items-center px-5 py-3.5"
          >
            <View
              style={{ flexDirection: "row", alignItems: "center", gap: 8 }}
            >
              <BookOpen
                size={14}
                color={EMBER.colors.textLight}
                strokeWidth={2}
              />
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 13,
                  fontWeight: "600",
                  color: EMBER.colors.textMid,
                }}
              >
                Sources ({references.length})
              </Text>
            </View>
          </AccordionTrigger>

          <AccordionContent className="pb-0">
            <View style={{ paddingHorizontal: 20, paddingBottom: 16, gap: 18 }}>
              {groups.map(([level, refs]) => (
                <View key={level} style={{ gap: 10 }}>
                  {level !== UNGROUPED_KEY && EVIDENCE_LEVELS[level] && (
                    <EvidenceLevelChip
                      level={level}
                      onPress={() => setModalLevel(level)}
                    />
                  )}
                  {refs.map((ref, i) => {
                    const citation = formatCitation(ref);
                    const hasUrl = Boolean(ref.url);
                    const Wrapper = hasUrl ? Pressable : View;
                    return (
                      <Wrapper
                        key={`${level}-${i}`}
                        onPress={
                          hasUrl
                            ? () => Linking.openURL(ref.url!).catch(() => {})
                            : undefined
                        }
                        style={{ gap: 2 }}
                      >
                        {citation ? (
                          <Text
                            className="font-dm-sans"
                            style={{
                              fontSize: 12,
                              fontWeight: "500",
                              color: EMBER.colors.textMid,
                              lineHeight: 17,
                            }}
                          >
                            {citation}
                          </Text>
                        ) : null}
                        <View
                          style={{
                            flexDirection: "row",
                            alignItems: "flex-start",
                            gap: 6,
                          }}
                        >
                          <Text
                            className="font-dm-sans"
                            style={{
                              flex: 1,
                              fontSize: 12,
                              fontStyle: "italic",
                              color: EMBER.colors.textLight,
                              lineHeight: 17,
                              marginTop: 1,
                            }}
                          >
                            {ref.title}
                          </Text>
                          {hasUrl ? (
                            <ExternalLink
                              size={11}
                              color={EMBER.colors.textFaint}
                              strokeWidth={1.75}
                              style={{ marginTop: 3 }}
                            />
                          ) : null}
                        </View>
                      </Wrapper>
                    );
                  })}
                </View>
              ))}
            </View>
          </AccordionContent>
        </AccordionItem>
      </Accordion>

      <EvidenceLevelModal
        level={modalLevel}
        visible={modalLevel !== null}
        onClose={() => setModalLevel(null)}
      />
    </View>
  );
}
